/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { AVATAR_FULL_IMAGE_SIZE, AVATAR_TEMP_IMAGE_SIZE } from '@constants/avatar-maker';
import fileDownload from 'js-file-download';
import Bugsnag from '@bugsnag/js';
import { trackEvent } from '@utils/analytics';
import { useUserContext } from '@contexts/user-context';
import { getGlobalState } from '@states/ProfileState';
import watermarkSmall from './img/avatar-watermark-small.png';
import watermarkLarge from './img/avatar-watermark-large.png';
import s from './DownloadPortrait.module.scss';

const propTypes = {
  children: PropTypes.node.isRequired,
};

const DownloadPortrait = ({ children }) => {
  const {
    profile: { avatar },
  } = useUserContext();
  const avatarImage = getGlobalState('userAvatarImage');
  const saveAvatarImageHolderRef = useRef();
  const watermarkSmallRef = useRef();
  const watermarkLargeRef = useRef();
  const saveAvatarCanvasRef = useRef();
  const [avatarCompositeComplete, setAvatarCompositeComplete] = useState(false);
  // If we are not using a local dataURL image then append a dummy query param to prevent
  // chrome from browser caching the avatar image with no Access-Control-Allow-Origin header.
  // See this thread for info on this issue:
  // https://serverfault.com/questions/856904/chrome-s3-cloudfront-no-access-control-allow-origin-header-on-initial-xhr-req
  let xhrAvatarImage = avatarImage;
  let canvasAvatarImage = avatarImage;

  if (avatarImage && !avatarImage.startsWith?.('data:image')) {
    // Temporarily replace ww domain with hp until backend is updated to return assets from harrypotter.com WWD-4167
    const fetchAvatarImage = avatarImage.replace('wizardingworld', 'harrypotter');

    xhrAvatarImage = `${fetchAvatarImage}?x-request-id=xhrAvatarImage`;
    canvasAvatarImage = `${fetchAvatarImage}?x-request-id=canvasAvatarImage`;
  }
  const saveAvatar = async () => {
    let blob;
    if (avatarCompositeComplete) {
      blob = await new Promise((resolve) => saveAvatarCanvasRef.current.toBlob(resolve));
    } else {
      const imageResponse = await fetch(xhrAvatarImage);
      blob = await imageResponse.blob();
    }

    fileDownload(blob, 'wizarding-world-portrait.png');

    const segmentProps = {
      avatarId: avatar?.avatarId,
    };
    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of Object.entries(avatar)) {
      if (avatar[key] && typeof avatar[key] === 'object') {
        segmentProps[key] = {
          content_id: value.contentfulId,
        };
        /* istanbul ignore else */
        if (value.variantContentfulId) {
          segmentProps[key].variant_id = value.variantContentfulId;
        }
      }
    }

    trackEvent('Avatar Downloaded', segmentProps);
  };

  // Composite the avatar image and a watermark into a hidden canvas that can
  // later be used as the downloaded "share" image.
  useEffect(() => {
    let animationId;
    const loadImageIntoCanvas = async () => {
      const canvas = saveAvatarCanvasRef.current;
      const imageHolder = saveAvatarImageHolderRef.current;

      // If the smaller, temp image is being used then use the smaller
      // watermark as well and size the canvas appropriately.
      const savedAvatarSize = avatarImage.startsWith?.('data:image')
        ? AVATAR_TEMP_IMAGE_SIZE
        : AVATAR_FULL_IMAGE_SIZE;
      const watermark = avatarImage.startsWith?.('data:image')
        ? watermarkSmallRef.current
        : watermarkLargeRef.current;

      canvas.width = savedAvatarSize;
      canvas.height = savedAvatarSize;

      // Make sure the avatar image and watermark are fully loaded before drawing
      // on the downloadable canvas.
      await Promise.all(
        [
          { imageRef: imageHolder, src: canvasAvatarImage },
          { imageRef: watermark, src: watermark.src },
        ].map((imageData) => {
          return new Promise((resolve) => {
            /* istanbul ignore next */
            if (imageData.imageRef.src && imageData.imageRef.complete) {
              resolve();
            } else {
              imageData.imageRef.addEventListener('load', resolve, { once: true });
              imageData.imageRef.addEventListener('error', resolve, { once: true });
              imageData.imageRef.setAttribute('crossOrigin', 'Anonymous');
              imageData.imageRef.setAttribute('src', imageData.imageRef.src);
            }
          });
        }),
      );

      animationId = requestAnimationFrame(() => {
        const context = canvas.getContext('2d');
        try {
          context.drawImage(imageHolder, 0, 0, savedAvatarSize, savedAvatarSize);
          context.drawImage(watermark, 0, 0, savedAvatarSize, savedAvatarSize);
          setAvatarCompositeComplete(true);
        } catch (e) {
          /* istanbul ignore next */
          Bugsnag.notify(e);
        }
      });
    };

    setAvatarCompositeComplete(false);
    loadImageIntoCanvas();
    return () => window.cancelAnimationFrame(animationId);
  }, [avatarImage]);

  return (
    <div className={s.downloadPortrait}>
      <div
        className={s.children}
        onClick={() => {
          saveAvatar();
        }}
        data-testid="downloadBtn"
      >
        {children}
      </div>
      <img
        src={canvasAvatarImage}
        ref={saveAvatarImageHolderRef}
        className={s.saveAvatarImageHolder}
        alt="save avatar holder"
        data-testid="avatarImageHolder"
      />
      <img
        src={watermarkSmall}
        ref={watermarkSmallRef}
        className={s.saveAvatarImageHolder}
        alt="small watermark holder"
      />
      <img
        src={watermarkLarge}
        ref={watermarkLargeRef}
        className={s.saveAvatarImageHolder}
        alt="large watermark holder"
      />
      <canvas
        ref={saveAvatarCanvasRef}
        className={s.fullAvatarCanvas}
        data-testid="fullAvatarCanvas"
      />
    </div>
  );
};

DownloadPortrait.propTypes = propTypes;
export default DownloadPortrait;
